import dynamic from 'next/dynamic';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {wrapper} from '@jobseeker/common-core/stores';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {isCSR} from '@jobseeker/lotus/utils';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp, utilServerSideDeviceDetection} from '@utils';
import {metaInfo} from '@configs';
import {GRIEVANCES} from '@constants';
import loadSEO from '@helpers/loadSEO';
import {InjectSearchOverlay} from '@components';
import GrievancesWidget from '@components/Static/Grievances';
import type {AppState} from '@jobseeker/common-core/types';
import type {GetServerSidePropsContext} from 'next/types';

type GrievancesProps = {
  seoConfig: object;
  isMobile?: boolean;
};

const HiristAuthentication = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
    ),
  {
    ssr: false,
  }
);

const Grievances = ({seoConfig, isMobile}: GrievancesProps) => {
  const metaData = getConfigByApp(metaInfo, GRIEVANCES, true, GRIEVANCES);
  const {searchOverlay, enableLoginPopup} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  const breadcrumbElements = isAppIimjobs
    ? [
        {
          name: 'Home',
          item: '/',
        },
        {
          name: 'Grievances',
          item: '',
        },
      ]
    : [];

  const onLoginSuccess = () => {
    if (isCSR()) {
      window.location.reload();
    }
  };

  return (
    <Interceptor metaInfo={metaData}>
      <SeoLayout seoConfig={seoConfig} />
      <NavLayout>
        <ResponsiveLayout
          breadcrumbElements={breadcrumbElements}
          variant={VersionVariants.VERSION_TWO}
        >
          {!isAppIimjobs && (
            <HiristAuthentication
              variant={VersionVariants.VERSION_TWO}
              onSuccess={onLoginSuccess}
              openDialog={enableLoginPopup}
              setOpenDialog={() => {
                dispatch({
                  type: 'AppContext/set_login_popup',
                  payload: false,
                });
              }}
            />
          )}
          <InjectSearchOverlay isSearchEnabled={searchOverlay} />
          <GrievancesWidget isMobile={isMobile} />
        </ResponsiveLayout>
      </NavLayout>
    </Interceptor>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const seoConfig = await loadSEO(GRIEVANCES);

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: GRIEVANCES},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    return {
      props: {
        seoConfig,
        isMobile,
      },
    };
  }
);

export default Grievances;
